/**
 * CEP Input Component
 * 
 * Input field for CEP with automatic address fetching and autofill.
 */
import React, { useState, useCallback, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, MapPin, Check, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCep, cleanCep, type AddressData } from '@/hooks/use-cep';

interface CepInputProps {
  value: string;
  onChange: (value: string) => void;
  onAddressFound?: (address: AddressData) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
  required?: boolean;
}

export function CepInput({
  value,
  onChange,
  onAddressFound,
  label = 'CEP',
  placeholder = '00000-000',
  error: externalError,
  disabled = false,
  className,
  required = false,
}: Readonly<CepInputProps>) {
  const { data, isLoading, error: fetchError, fetchAddress, clearData } = useCep();
  const [hasSearched, setHasSearched] = useState(false);

  // Format CEP as user types
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value.replace(/\D/g, '');
    
    // Limit to 8 digits
    if (newValue.length > 8) {
      newValue = newValue.slice(0, 8);
    }
    
    // Format with dash
    if (newValue.length > 5) {
      newValue = `${newValue.slice(0, 5)}-${newValue.slice(5)}`;
    }
    
    onChange(newValue);
    
    // Clear previous data when user changes the CEP
    if (hasSearched) {
      clearData();
      setHasSearched(false);
    }
  }, [onChange, clearData, hasSearched]);

  // Fetch address when CEP is complete
  useEffect(() => {
    const cleaned = cleanCep(value);
    
    if (cleaned.length === 8 && !hasSearched) {
      fetchAddress(value).then((address) => {
        setHasSearched(true);
        if (address && onAddressFound) {
          onAddressFound(address);
        }
      });
    }
  }, [value, fetchAddress, onAddressFound, hasSearched]);

  // Determine status icon
  const renderStatusIcon = () => {
    if (isLoading) {
      return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
    }
    
    if (data && !fetchError) {
      return <Check className="h-4 w-4 text-emerald-500" />;
    }
    
    if (fetchError || externalError) {
      return <AlertCircle className="h-4 w-4 text-destructive" />;
    }
    
    return <MapPin className="h-4 w-4 text-muted-foreground" />;
  };

  const error = externalError || fetchError;

  return (
    <div className={cn('grid gap-2', className)}>
      {label && (
        <Label htmlFor="cep">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}
      
      <div className="relative">
        <Input
          id="cep"
          type="text"
          inputMode="numeric"
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled || isLoading}
          className={cn(
            'pr-10',
            error && 'border-destructive focus-visible:ring-destructive'
          )}
          maxLength={9} // 00000-000
        />
        
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {renderStatusIcon()}
        </div>
      </div>
      
      {error && (
        <p className="text-xs text-destructive">{error}</p>
      )}
      
      {data && !error && (
        <p className="text-xs text-muted-foreground">
          {data.street && `${data.street}, `}
          {data.neighborhood} - {data.city}/{data.state}
        </p>
      )}
    </div>
  );
}

export default CepInput;

